import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Transition from '../../../utils/Transition';

function TaxFilePagination({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  onLimitChange,
  currentLimit = 10,
  totalItems = 0,
}) {
  const [limitDropdownOpen, setLimitDropdownOpen] = useState(false);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !limitDropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setLimitDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // Close dropdown on escape key
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!limitDropdownOpen || keyCode !== 27) return;
      setLimitDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  // Calculate pagination info
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * currentLimit + 1;
  const endItem = Math.min(currentPage * currentLimit, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();
  const limitOptions = [5, 10, 25, 50, 100];

  const handlePageClick = page => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const handleLimitChange = newLimit => {
    onLimitChange(newLimit);
    setLimitDropdownOpen(false);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      {/* Page size selector */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <span>Show</span>
        <div className="relative">
          <button
            ref={trigger}
            className="btn px-3 py-1 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-300 min-w-[60px]"
            onClick={() => setLimitDropdownOpen(!limitDropdownOpen)}
            aria-expanded={limitDropdownOpen}
            aria-haspopup="listbox"
            aria-label={`Show ${currentLimit} entries per page`}
          >
            {currentLimit}
            <svg
              className="shrink-0 ml-1 fill-current text-gray-400 dark:text-gray-500"
              width="8"
              height="5"
              viewBox="0 0 8 5"
            >
              <path d="M4 5L0 1h8L4 5z" />
            </svg>
          </button>
          <Transition
            show={limitDropdownOpen}
            tag="div"
            className="z-10 absolute top-full left-0 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1 rounded-lg shadow-lg overflow-hidden mt-1"
            enter="transition ease-out duration-100 transform"
            enterStart="opacity-0 -translate-y-2"
            enterEnd="opacity-100 translate-y-0"
            leave="transition ease-out duration-100"
            leaveStart="opacity-100"
            leaveEnd="opacity-0"
          >
            <div ref={dropdown}>
              {limitOptions.map(option => (
                <button
                  key={option}
                  className={`w-full text-left px-3 py-1 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/20 focus:bg-gray-50 dark:focus:bg-gray-700/20 focus:outline-none ${
                    option === currentLimit
                      ? 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                  onClick={() => handleLimitChange(option)}
                  role="option"
                  aria-selected={option === currentLimit}
                >
                  {option}
                </button>
              ))}
            </div>
          </Transition>
        </div>
        <span>entries</span>
      </div>

      {/* Results info */}
      <div className="text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
        Showing{' '}
        <span className="font-medium text-gray-600 dark:text-gray-300">
          {startItem}
        </span>{' '}
        to{' '}
        <span className="font-medium text-gray-600 dark:text-gray-300">
          {endItem}
        </span>{' '}
        of{' '}
        <span className="font-medium text-gray-600 dark:text-gray-300">
          {totalItems}
        </span>{' '}
        results
      </div>

      {/* Navigation */}
      <nav
        className="flex justify-center sm:justify-end"
        role="navigation"
        aria-label="Pagination Navigation"
      >
        <ul className="flex items-center gap-1">
          {/* Previous button */}
          <li>
            <button
              className={`btn px-3 py-2 ${
                currentPage === 1
                  ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:ring-offset-2'
              }`}
              onClick={() => handlePageClick(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Go to previous page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </li>

          {/* Page numbers */}
          {pageNumbers.map(page => (
            <li key={page}>
              <button
                className={`btn px-3 py-2 min-w-[40px] ${
                  page === currentPage
                    ? 'bg-violet-600 border-violet-600 text-white hover:bg-violet-700 focus:ring-2 focus:ring-violet-500 focus:ring-offset-2'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:ring-offset-2'
                }`}
                onClick={() => handlePageClick(page)}
                aria-label={`Go to page ${page}`}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </button>
            </li>
          ))}

          {/* Next button */}
          <li>
            <button
              className={`btn px-3 py-2 ${
                currentPage === totalPages
                  ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:ring-offset-2'
              }`}
              onClick={() => handlePageClick(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Go to next page"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
}

TaxFilePagination.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onLimitChange: PropTypes.func.isRequired,
  currentLimit: PropTypes.number,
  totalItems: PropTypes.number,
};

export default TaxFilePagination;
